import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [pricingData, setPricingData] = useState(null);

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  useEffect(() => {
    fetch(`${API_URL}/api/content/pricing`)
      .then(res => res.json())
      .then(setPricingData)
      .catch(() => {});
  }, []);

  if (!pricingData) {
    return (
      <div className="min-h-screen bg-[#F5F0EB] flex items-center justify-center">
        <div className="text-[#6B6B6B] text-sm">Loading pricing...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0EB]">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className={`container transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-3xl">
            <h1 className="heading-display mb-6">Pricing</h1>
            <p className="body-large">
              Clear, straightforward pricing with no hidden costs. All pricing is confirmed before booking.
            </p>
          </div>
        </div>
      </section>

      {/* Base Pricing */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="mb-12">
            <h2 className="heading-large mb-4">1-to-1 Personal Training</h2>
            <p className="body-regular flex items-center gap-3">
              <span className="inline-flex items-center gap-1 bg-[#C9A962] text-[#1A1A1A] px-3 py-1 text-xs font-semibold tracking-[0.1em] uppercase">
                Base Location
              </span>
              Anytime Fitness Leyton
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingData.baseLocation.prices.map((item, index) => (
              <div 
                key={index} 
                className={`border p-8 text-center relative ${
                  item.popular ? 'border-[#C9A962] border-2' : 'border-[#E5E0DB]'
                }`}
              >
                {item.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A962] text-[#1A1A1A] text-[10px] font-semibold tracking-[0.1em] uppercase px-4 py-1">
                    Most Popular
                  </span>
                )}
                <h3 className="heading-small mb-2">{item.type}</h3>
                <p className="text-xs text-[#6B6B6B] mb-4">{item.duration}</p>
                <div className="mb-4">
                  <span className="text-4xl font-semibold text-[#1A1A1A]" style={{fontFamily: "'Oswald', sans-serif"}}>£{item.price}</span>
                </div>
                {item.savings && (
                  <span className="text-xs font-semibold text-[#C9A962]">
                    {item.savings}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 1-to-1 Boxing Coaching */}
      <section className="bg-[#EDE8E3] py-20 md:py-28">
        <div className="container">
          <div className="mb-12">
            <h2 className="heading-large mb-4">1-to-1 Boxing Coaching</h2>
            <p className="body-regular text-[#6B6B6B] mb-6 max-w-3xl">
              Pad work, boxing fundamentals, and high-intensity conditioning sessions designed to improve fitness, coordination, and confidence. Suitable for beginners and experienced clients.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3 max-w-2xl">
              {[
                "Pad work & boxing technique",
                "Fat loss & conditioning",
                "Beginner-friendly coaching",
                "Great for stress relief and confidence"
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-[#C9A962] flex-shrink-0" />
                  <span className="text-sm text-[#6B6B6B]">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { type: "Pay As You Go", duration: "1 hour", price: 65, perSession: "£65 per session" },
              { type: "5 Sessions", duration: "5 hours", price: 300, perSession: "£60 per session", savings: "Save £25" },
              { type: "10 Sessions", duration: "10 hours", price: 550, perSession: "£55 per session", savings: "Save £100", popular: true },
              { type: "20 Sessions", duration: "20 hours", price: 1000, perSession: "£50 per session", savings: "Save £300" }
            ].map((item) => (
              <div 
                key={item.type} 
                className={`bg-white border p-8 text-center relative ${
                  item.popular ? 'border-[#C9A962] border-2' : 'border-[#E5E0DB]'
                }`}
              >
                {item.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A962] text-[#1A1A1A] text-[10px] font-semibold tracking-[0.1em] uppercase px-4 py-1">
                    Best Value
                  </span>
                )}
                <h3 className="heading-small mb-2">{item.type}</h3>
                <p className="text-xs text-[#6B6B6B] mb-4">{item.duration}</p>
                <div className="mb-2">
                  <span className="text-4xl font-semibold text-[#1A1A1A]" style={{fontFamily: "'Oswald', sans-serif"}}>£{item.price}</span>
                </div>
                <p className="text-xs text-[#6B6B6B] mb-3">{item.perSession}</p>
                {item.savings && (
                  <p className="text-xs font-semibold text-[#C9A962] mb-4">
                    {item.savings}
                  </p>
                )}
                <Link 
                  to="/contact" 
                  className="inline-block w-full py-3 bg-[#1A1A1A] text-white text-xs font-semibold tracking-[0.1em] uppercase hover:bg-[#333] transition-colors duration-300"
                >
                  Book Now
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Online Coaching */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="mb-12">
            <h2 className="heading-large mb-4">Online Coaching</h2>
            <p className="body-regular text-[#6B6B6B] max-w-3xl">
              Online coaching designed to help you build strength, lose fat, and stay consistent with expert guidance and accountability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {/* 8-Week Online Coaching Plan */}
            <div className="border-2 border-[#C9A962] p-8 flex flex-col relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A962] text-[#1A1A1A] text-[10px] font-semibold tracking-[0.1em] uppercase px-4 py-1">
                Get Started
              </span>
              <h3 className="heading-small mb-2">8-Week Online Coaching Plan</h3>
              <div className="mb-6">
                <span className="text-4xl font-semibold text-[#1A1A1A]" style={{fontFamily: "'Oswald', sans-serif"}}>£150</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  "Personalised training programme",
                  "Training app with exercise demonstrations",
                  "Weekly progress check-ins",
                  "Habit and lifestyle guidance",
                  "Progress tracking",
                  "Messaging support",
                  "Programme adjustments throughout the 8 weeks"
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <CheckCircle size={14} className="text-[#C9A962] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#6B6B6B]">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/contact" 
                className="inline-block w-full py-3 bg-[#1A1A1A] text-white text-xs font-semibold tracking-[0.1em] uppercase text-center hover:bg-[#333] transition-colors duration-300"
              >
                Start Coaching
              </Link>
            </div>

            {/* Ongoing Coaching (Renewal) */}
            <div className="border border-[#E5E0DB] p-8 flex flex-col">
              <h3 className="heading-small mb-2">Ongoing Coaching (Renewal)</h3>
              <div className="mb-6">
                <span className="text-4xl font-semibold text-[#1A1A1A]" style={{fontFamily: "'Oswald', sans-serif"}}>£120</span>
                <span className="text-sm text-[#6B6B6B] ml-2">per month</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  "Updated training programme",
                  "Continued training app access",
                  "Weekly check-ins",
                  "Progress tracking",
                  "Habit and lifestyle coaching",
                  "Messaging support",
                  "Programme updates as you progress"
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <CheckCircle size={14} className="text-[#C9A962] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#6B6B6B]">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/contact" 
                className="inline-block w-full py-3 bg-[#1A1A1A] text-white text-xs font-semibold tracking-[0.1em] uppercase text-center hover:bg-[#333] transition-colors duration-300"
              >
                Enquire Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Location Adjustments */}
      <section className="bg-[#F5F0EB] py-20 md:py-28">
        <div className="container">
          <h2 className="heading-large mb-8">Location-Based Adjustments</h2>
          <p className="body-regular mb-8 text-[#6B6B6B]">
            Sessions at premium gyms incur an additional charge per hour:
          </p>
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl">
            {pricingData.gymAdjustments.map((gym) => (
              <div key={gym.name} className="bg-white border border-[#E5E0DB] p-6">
                <h3 className="heading-small mb-2">{gym.name}</h3>
                <p className="text-2xl font-semibold text-[#C9A962]" style={{fontFamily: "'Oswald', sans-serif"}}>{gym.adjustment}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Options */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <h2 className="heading-large mb-8">Other Training Options</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl">
            {pricingData.otherOptions.map((option) => (
              <div key={option.name} className="border border-[#E5E0DB] p-6">
                <h3 className="heading-small mb-2">{option.name}</h3>
                <div className="mb-3">
                  <span className="text-3xl font-semibold text-[#1A1A1A]" style={{fontFamily: "'Oswald', sans-serif"}}>£{option.price}</span>
                  <span className="text-sm text-[#6B6B6B] ml-2">per session</span>
                </div>
                <p className="text-xs text-[#6B6B6B]">{option.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Training */}
      <section className="bg-[#EDE8E3] py-20 md:py-28">
        <div className="container">
          <h2 className="heading-large mb-4">Partner Training</h2>
          <p className="body-regular mb-8 text-[#6B6B6B]">
            Train with a friend or partner. Price per session (1 hour) for 2 people:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full max-w-2xl bg-white">
              <thead>
                <tr className="border-b border-[#E5E0DB]">
                  <th className="text-left py-4 px-6 heading-small">Location</th>
                  <th className="text-right py-4 px-6 heading-small">Price</th>
                </tr>
              </thead>
              <tbody>
                {pricingData.partnerTraining.map((item) => (
                  <tr key={item.location} className="border-b border-[#E5E0DB]">
                    <td className="py-4 px-6 body-regular">{item.location}</td>
                    <td className="py-4 px-6 text-right font-semibold text-lg" style={{fontFamily: "'Oswald', sans-serif"}}>£{item.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Group Training */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-2xl">
            <h2 className="heading-large mb-4">Group Training</h2>
            <p className="body-regular mb-6 text-[#6B6B6B]">
              Small group sessions with a maximum of 4 people. Get the benefits of personal training 
              in a motivating group environment at a more accessible price point.
            </p>
            <div className="border border-[#C9A962] bg-[#F5F0EB] p-6">
              <div className="flex items-start gap-4">
                <Info size={24} className="text-[#C9A962] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="heading-small mb-2">Enquire for Pricing</h3>
                  <p className="body-regular text-[#6B6B6B] mb-4">
                    Group training prices vary based on location, session frequency, and group size. 
                    Contact me for a custom quote.
                  </p>
                  <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-[#C9A962] hover:text-[#1A1A1A] transition-colors duration-300">
                    Get in touch
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confirmation Note */}
      <section className="bg-[#F5F0EB] py-12">
        <div className="container">
          <div className="max-w-2xl mx-auto flex gap-4 p-6 border border-[#C9A962] bg-white">
            <CheckCircle size={24} className="text-[#C9A962] flex-shrink-0 mt-1" />
            <div>
              <h3 className="heading-small mb-2">Pricing Confirmation</h3>
              <p className="body-regular text-[#6B6B6B]">
                All pricing is confirmed before booking to ensure complete transparency. 
                Session packages do not expire.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1A1A1A] py-20 md:py-28">
        <div className="container text-center">
          <h2 className="heading-large text-white mb-6">Questions About Pricing?</h2>
          <p className="body-large text-gray-400 mb-10 max-w-xl mx-auto">
            Book a free consultation to discuss the best option for your goals and budget.
          </p>
          <Link to="/contact" className="btn-gold inline-flex items-center gap-2">
            Book Free Consultation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
